/**
 * LineChart Component
 *
 * Displays time-series data as a line graph using Recharts.
 * Supports single or multi-dimensional metrics with responsive design.
 *
 * Performance: Uses ChartDataContext for cached data - no individual API calls
 */

import PropTypes from 'prop-types';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useChartData } from '../../contexts/ChartDataContext';
import { formatDate, formatMetricValue, formatAxisValue } from '../../utils/formatters';
import './ChartStyles.css';

const LineChart = ({ chartId }) => {
  // Get chart data from cache (loaded via batch request in template)
  const { getChartData: getCachedChartData } = useChartData();
  const cachedData = getCachedChartData(chartId);

  const { data, loading, error } = cachedData;

  if (loading) {
    return (
      <div className="chart-container loading">
        <div className="spinner"></div>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    return (
      <div className="chart-container no-data">
        <p>No data available</p>
      </div>
    );
  }

  // Extract chart configuration
  const {
    chart_name,
    metric_name,
    metric_unit,
    x_label,
    y_label,
    data_points,
    dimensions,
  } = data;

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{formatDate(label, 'short')}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: {formatMetricValue(entry.value, metric_unit)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format X-axis labels based on date format
  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';

    // Determine format based on data granularity
    const date = new Date(dateStr);
    const firstDate = new Date(data_points[0]?.date);
    const lastDate = new Date(data_points[data_points.length - 1]?.date);

    // Calculate time span in days
    const timeSpanDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

    // Format based on time span
    if (timeSpanDays <= 90) {
      // Daily/Weekly: Show day and month
      return formatDate(dateStr, 'short');
    } else if (timeSpanDays <= 730) {
      // Monthly: Show month and year
      return formatDate(dateStr, 'month');
    } else {
      // Yearly/Multi-year: Show just year
      return formatDate(dateStr, 'year');
    }
  };

  // Prepare data for Recharts
  // Handle both single-series and multi-dimensional data
  let chartData;
  let lineKeys;

  if (dimensions && dimensions.length > 0) {
    // Multi-dimensional: pivot data by dimension
    const pivotedData = {};

    data_points.forEach(point => {
      const date = point.date;
      const dimension = point.dimension || 'Unknown';
      const value = point.value;

      if (!pivotedData[date]) {
        pivotedData[date] = { date };
      }
      pivotedData[date][dimension] = value;
    });

    chartData = Object.values(pivotedData).sort((a, b) =>
      new Date(a.date) - new Date(b.date)
    );
    lineKeys = dimensions;
  } else {
    // Single series
    chartData = data_points.map(point => ({
      date: point.date,
      value: point.value,
    }));
    lineKeys = ['value'];
  }

  // Color palette for multiple lines
  const colors = [
    '#3498db', // Blue
    '#e74c3c', // Red
    '#2ecc71', // Green
    '#f39c12', // Orange
    '#9b59b6', // Purple
    '#1abc9c', // Teal
    '#34495e', // Dark gray
    '#e67e22', // Dark orange
  ];

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3 className="chart-title">{chart_name || metric_name}</h3>
        <p className="chart-subtitle">{y_label || metric_name}</p>
      </div>

      <div className="chart-body">
        <ResponsiveContainer width="100%" height={400}>
          <RechartsLineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatAxisValue(value, metric_unit)}
            />
            <Tooltip content={<CustomTooltip />} />
            {lineKeys.length > 1 && <Legend />}

            {lineKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={key === 'value' ? metric_name : key}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

LineChart.propTypes = {
  chartId: PropTypes.string.isRequired,
};

export default LineChart;
